import * as Tabs from '@radix-ui/react-tabs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BookOpen,
  ChevronLeft,
  FileText,
  Library,
  LoaderCircle,
  Plus,
  Save,
  Search,
  Sparkles,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { api } from '../lib/api';

interface Overview {
  collections: number;
  documents: number;
  commands: number;
  prompts: number;
  modes: number;
}

interface Collection {
  id: string;
  key: string;
  nameAr: string;
  descriptionAr: string | null;
  scope: string;
  active: boolean;
  _count: { documents: number };
}

interface KnowledgeDocument {
  id: string;
  titleAr: string;
  sourceName: string;
  sourceUrl: string | null;
  content: string;
  contentHash: string;
  updatedAt: string;
  collection: { key: string; nameAr: string };
}

interface Command {
  id: string;
  commandKey: string;
  displayNameAr: string;
  descriptionAr: string;
  categoryAr: string;
  modeKey: string;
  active: boolean;
}

interface Prompt {
  id: string;
  key: string;
  nameAr: string;
  descriptionAr: string;
  level: 'SYSTEM' | 'OFFICE' | 'PERSONAL';
  modeKey: string | null;
  version: number;
  updatedAt: string;
  systemPromptEn?: string;
}

const scopeLabels: Record<string, string> = {
  global: 'عام',
  engineering: 'هندسي',
  mode: 'وضع',
  project: 'مشروع',
  personal: 'شخصي',
};

const promptLevelLabels = {
  SYSTEM: 'مقيد — النظام',
  OFFICE: 'يدير المشرف — المكتب',
  PERSONAL: 'يدير المستخدم — شخصي',
} as const;

function KnowledgeLibrary() {
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCollectionForm, setShowCollectionForm] = useState(false);
  const [collectionForm, setCollectionForm] = useState({
    key: '',
    nameAr: '',
    descriptionAr: '',
    scope: 'engineering',
  });
  const [documentForm, setDocumentForm] = useState({
    titleAr: '',
    sourceName: '',
    sourceUrl: '',
    content: '',
  });

  const collectionsQuery = useQuery({
    queryKey: ['knowledge-collections'],
    queryFn: () => api<Collection[]>('/api/knowledge/collections'),
  });

  useEffect(() => {
    if (!selectedId && collectionsQuery.data?.[0]) {
      setSelectedId(collectionsQuery.data[0].id);
    }
  }, [selectedId, collectionsQuery.data]);

  const documentsQuery = useQuery({
    queryKey: ['knowledge-documents', selectedId],
    queryFn: () =>
      api<KnowledgeDocument[]>(
        `/api/knowledge/documents${selectedId ? `?collectionId=${selectedId}` : ''}`,
      ),
    enabled: Boolean(selectedId),
  });

  const createCollection = useMutation({
    mutationFn: () =>
      api<Collection>('/api/knowledge/collections', {
        method: 'POST',
        body: JSON.stringify({
          key: collectionForm.key,
          nameAr: collectionForm.nameAr,
          ...(collectionForm.descriptionAr
            ? { descriptionAr: collectionForm.descriptionAr }
            : {}),
          scope: collectionForm.scope,
        }),
      }),
    onSuccess: async (collection) => {
      setSelectedId(collection.id);
      setCollectionForm({ key: '', nameAr: '', descriptionAr: '', scope: 'engineering' });
      setShowCollectionForm(false);
      toast.success('أُنشئت مجموعة المعرفة');
      await queryClient.invalidateQueries({ queryKey: ['knowledge-collections'] });
    },
    onError: (error) => toast.error(error.message),
  });

  const createDocument = useMutation({
    mutationFn: () =>
      api<KnowledgeDocument>('/api/knowledge/documents', {
        method: 'POST',
        body: JSON.stringify({
          collectionId: selectedId,
          titleAr: documentForm.titleAr,
          sourceName: documentForm.sourceName,
          ...(documentForm.sourceUrl ? { sourceUrl: documentForm.sourceUrl } : {}),
          content: documentForm.content,
        }),
      }),
    onSuccess: async () => {
      setDocumentForm({ titleAr: '', sourceName: '', sourceUrl: '', content: '' });
      toast.success('أُضيف المستند إلى المعرفة');
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['knowledge-documents', selectedId] }),
        queryClient.invalidateQueries({ queryKey: ['knowledge-collections'] }),
      ]);
    },
    onError: (error) => toast.error(error.message),
  });

  const selectedCollection = collectionsQuery.data?.find((item) => item.id === selectedId);

  return (
    <div className="grid min-h-[calc(100vh-0px)] lg:grid-cols-[300px_1fr]">
      <aside className="border-l border-ink/10 bg-white/60 p-4">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <p className="text-[10px] font-bold text-slate-400">المصادر المحلية</p>
            <h2 className="mt-1 text-lg font-black">المجموعات</h2>
          </div>
          <button
            type="button"
            onClick={() => setShowCollectionForm((value) => !value)}
            className="grid h-9 w-9 place-items-center rounded-xl bg-ink text-white hover:bg-teal"
            aria-label="إضافة مجموعة"
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>

        {showCollectionForm && (
          <div className="mb-4 space-y-2 rounded-2xl border border-teal/20 bg-teal/5 p-3">
            <input
              value={collectionForm.nameAr}
              onChange={(event) => setCollectionForm({ ...collectionForm, nameAr: event.target.value })}
              placeholder="اسم المجموعة"
              className="w-full rounded-lg border border-ink/10 bg-white px-2.5 py-2 text-[11px] outline-none focus:border-teal"
            />
            <input
              value={collectionForm.key}
              onChange={(event) => setCollectionForm({ ...collectionForm, key: event.target.value.toLowerCase() })}
              placeholder="internal_key"
              dir="ltr"
              className="w-full rounded-lg border border-ink/10 bg-white px-2.5 py-2 text-[11px] outline-none focus:border-teal"
            />
            <select
              value={collectionForm.scope}
              onChange={(event) => setCollectionForm({ ...collectionForm, scope: event.target.value })}
              className="w-full rounded-lg border border-ink/10 bg-white px-2.5 py-2 text-[11px] outline-none focus:border-teal"
            >
              {Object.entries(scopeLabels).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
            <textarea
              value={collectionForm.descriptionAr}
              onChange={(event) => setCollectionForm({ ...collectionForm, descriptionAr: event.target.value })}
              placeholder="وصف مختصر"
              rows={2}
              className="w-full resize-none rounded-lg border border-ink/10 bg-white px-2.5 py-2 text-[11px] outline-none focus:border-teal"
            />
            <button
              type="button"
              onClick={() => createCollection.mutate()}
              disabled={!collectionForm.nameAr || !collectionForm.key || createCollection.isPending}
              className="w-full rounded-lg bg-teal px-3 py-2 text-[10px] font-black text-white disabled:opacity-40"
            >
              إنشاء المجموعة
            </button>
          </div>
        )}

        <div className="space-y-1">
          {collectionsQuery.data?.map((collection) => (
            <button
              key={collection.id}
              type="button"
              onClick={() => setSelectedId(collection.id)}
              className={`w-full rounded-xl px-3 py-3 text-start transition ${
                selectedId === collection.id ? 'bg-ink text-white' : 'hover:bg-white'
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="truncate text-xs font-black">{collection.nameAr}</span>
                <span className={`rounded-full px-2 py-0.5 text-[8px] font-black ${
                  selectedId === collection.id ? 'bg-white/10 text-white/60' : 'bg-slate-100 text-slate-500'
                }`}>
                  {collection._count.documents}
                </span>
              </div>
              <p className={`mt-1 text-[9px] ${selectedId === collection.id ? 'text-white/40' : 'text-slate-400'}`}>
                {scopeLabels[collection.scope] || collection.scope}
              </p>
            </button>
          ))}
        </div>
      </aside>

      <section className="p-4 sm:p-7">
        <header className="mb-5 flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-[10px] font-black text-teal">
              {selectedCollection ? selectedCollection.nameAr : 'اختر مجموعة'}
            </p>
            <h2 className="mt-1 text-xl font-black">مستندات المعرفة</h2>
            <p className="mt-1 text-[11px] font-semibold text-slate-500">
              المحتوى يبقى داخل Details، وكل مستند يحتفظ ببصمة إصدار.
            </p>
          </div>
          <span className="rounded-xl border border-ink/10 bg-white px-3 py-2 text-[10px] font-bold text-slate-500">
            {documentsQuery.data?.length || 0} مستند
          </span>
        </header>

        {selectedId && (
          <div className="mb-6 rounded-2xl border border-ink/10 bg-white p-4">
            <div className="mb-3 flex items-center gap-2">
              <FileText className="h-4 w-4 text-teal" />
              <h3 className="text-sm font-black">إضافة معرفة نصية</h3>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <input
                value={documentForm.titleAr}
                onChange={(event) => setDocumentForm({ ...documentForm, titleAr: event.target.value })}
                placeholder="عنوان المستند"
                className="rounded-xl border border-ink/10 bg-slate-50 px-3 py-2.5 text-xs outline-none focus:border-teal"
              />
              <input
                value={documentForm.sourceName}
                onChange={(event) => setDocumentForm({ ...documentForm, sourceName: event.target.value })}
                placeholder="اسم المصدر"
                className="rounded-xl border border-ink/10 bg-slate-50 px-3 py-2.5 text-xs outline-none focus:border-teal"
              />
            </div>
            <textarea
              value={documentForm.content}
              onChange={(event) => setDocumentForm({ ...documentForm, content: event.target.value })}
              placeholder="ألصق النص أو القاعدة المعرفية…"
              rows={5}
              className="mt-3 w-full resize-y rounded-xl border border-ink/10 bg-slate-50 px-3 py-3 text-xs leading-6 outline-none focus:border-teal"
            />
            <button
              type="button"
              onClick={() => createDocument.mutate()}
              disabled={
                !documentForm.titleAr ||
                !documentForm.sourceName ||
                documentForm.content.trim().length < 10 ||
                createDocument.isPending
              }
              className="mt-3 inline-flex items-center gap-2 rounded-xl bg-teal px-4 py-2.5 text-[10px] font-black text-white disabled:opacity-40"
            >
              {createDocument.isPending ? <LoaderCircle className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              حفظ في المعرفة
            </button>
          </div>
        )}

        {documentsQuery.isLoading ? (
          <div className="grid min-h-48 place-items-center"><LoaderCircle className="h-6 w-6 animate-spin text-teal" /></div>
        ) : documentsQuery.data?.length ? (
          <div className="space-y-3">
            {documentsQuery.data.map((document) => (
              <article key={document.id} className="rounded-2xl border border-ink/10 bg-white p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-sm font-black">{document.titleAr}</h3>
                    <p className="mt-1 text-[10px] font-bold text-slate-400">
                      {document.sourceName} · {document.collection.nameAr}
                    </p>
                  </div>
                  <span className="rounded-lg bg-slate-50 px-2 py-1 font-mono text-[8px] text-slate-400" dir="ltr">
                    {document.contentHash.slice(0, 10)}
                  </span>
                </div>
                <p className="mt-3 line-clamp-3 whitespace-pre-wrap text-[11px] font-semibold leading-6 text-slate-600">
                  {document.content}
                </p>
              </article>
            ))}
          </div>
        ) : (
          <div className="rounded-2xl border border-dashed border-ink/15 px-5 py-16 text-center">
            <BookOpen className="mx-auto mb-3 h-7 w-7 text-teal" />
            <p className="text-sm font-black">لا توجد مستندات في هذه المجموعة</p>
            <p className="mt-1 text-[10px] font-semibold text-slate-500">أضف أول قاعدة أو مرجع نصي من النموذج أعلاه.</p>
          </div>
        )}
      </section>
    </div>
  );
}

function CommandLibrary() {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('');
  const params = new URLSearchParams();
  if (query) params.set('q', query);
  if (category) params.set('category', category);
  const commandsQuery = useQuery({
    queryKey: ['commands', query, category],
    queryFn: () =>
      api<{ commands: Command[]; categories: string[] }>(
        `/api/knowledge/commands?${params.toString()}`,
      ),
  });

  const categories = useMemo(
    () => commandsQuery.data?.categories || [],
    [commandsQuery.data?.categories],
  );

  return (
    <div>
      <div className="mb-5 rounded-2xl border border-ink/10 bg-white p-4">
        <div className="flex flex-wrap gap-3">
          <label className="relative min-w-64 flex-1">
            <Search className="absolute end-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="ابحث في الأوامر بالعربية…"
              className="w-full rounded-xl border border-ink/10 bg-slate-50 px-3 py-2.5 pe-10 text-xs outline-none focus:border-teal"
            />
          </label>
          <select
            value={category}
            onChange={(event) => setCategory(event.target.value)}
            className="rounded-xl border border-ink/10 bg-slate-50 px-3 py-2.5 text-xs outline-none focus:border-teal"
          >
            <option value="">كل الفئات</option>
            {categories.map((item) => <option key={item} value={item}>{item}</option>)}
          </select>
        </div>
      </div>

      {commandsQuery.isLoading ? (
        <div className="grid min-h-48 place-items-center"><LoaderCircle className="h-6 w-6 animate-spin text-teal" /></div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {commandsQuery.data?.commands.map((command, index) => (
            <article key={command.id} className="group rounded-2xl border border-ink/10 bg-white p-4 transition hover:-translate-y-0.5 hover:border-teal/30 hover:shadow-panel">
              <div className="mb-3 flex items-center justify-between">
                <span className="grid h-8 w-8 place-items-center rounded-lg bg-teal/10 text-[10px] font-black text-teal">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <span className="rounded-full bg-slate-50 px-2 py-1 text-[8px] font-bold text-slate-500">
                  {command.categoryAr}
                </span>
              </div>
              <h3 className="text-sm font-black">{command.displayNameAr}</h3>
              <p className="mt-2 line-clamp-2 text-[11px] font-semibold leading-5 text-slate-500">
                {command.descriptionAr}
              </p>
              <button
                type="button"
                onClick={() => {
                  void navigator.clipboard.writeText(command.displayNameAr);
                  toast.success('نُسخ اسم الأمر');
                }}
                className="mt-4 inline-flex items-center gap-1 text-[9px] font-black text-teal"
              >
                نسخ اسم الأمر <ChevronLeft className="h-3 w-3" />
              </button>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

function PromptVault() {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    key: '',
    nameAr: '',
    descriptionAr: '',
    level: 'OFFICE',
    systemPromptEn: '',
  });
  const promptsQuery = useQuery({
    queryKey: ['prompts'],
    queryFn: () => api<Prompt[]>('/api/knowledge/prompts'),
  });
  const createPrompt = useMutation({
    mutationFn: () =>
      api('/api/knowledge/prompts', {
        method: 'POST',
        body: JSON.stringify({
          ...form,
          descriptionAr: form.descriptionAr,
          systemPromptEn: form.systemPromptEn,
        }),
      }),
    onSuccess: async () => {
      setForm({ key: '', nameAr: '', descriptionAr: '', level: 'OFFICE', systemPromptEn: '' });
      setShowForm(false);
      toast.success('أُنشئ إصدار Prompt جديد');
      await queryClient.invalidateQueries({ queryKey: ['prompts'] });
    },
    onError: (error) => toast.error(error.message),
  });

  return (
    <div>
      <div className="mb-5 flex justify-end">
        <button
          type="button"
          onClick={() => setShowForm((value) => !value)}
          className="inline-flex items-center gap-2 rounded-xl bg-ink px-4 py-2.5 text-[10px] font-black text-white hover:bg-teal"
        >
          <Plus className="h-3.5 w-3.5" /> إصدار Prompt
        </button>
      </div>

      {showForm && (
        <div className="mb-5 grid gap-3 rounded-2xl border border-teal/20 bg-teal/5 p-4 lg:grid-cols-2">
          <input value={form.nameAr} onChange={(event) => setForm({ ...form, nameAr: event.target.value })} placeholder="الاسم العربي" className="rounded-xl border border-ink/10 bg-white px-3 py-2.5 text-xs outline-none focus:border-teal" />
          <input value={form.key} onChange={(event) => setForm({ ...form, key: event.target.value.toLowerCase() })} placeholder="prompt_key" dir="ltr" className="rounded-xl border border-ink/10 bg-white px-3 py-2.5 text-xs outline-none focus:border-teal" />
          <select value={form.level} onChange={(event) => setForm({ ...form, level: event.target.value })} className="rounded-xl border border-ink/10 bg-white px-3 py-2.5 text-xs outline-none focus:border-teal">
            <option value="SYSTEM">System — مقيد</option>
            <option value="OFFICE">Office — يدير المشرف</option>
            <option value="PERSONAL">Personal — للمستخدم</option>
          </select>
          <input value={form.descriptionAr} onChange={(event) => setForm({ ...form, descriptionAr: event.target.value })} placeholder="وصف عربي" className="rounded-xl border border-ink/10 bg-white px-3 py-2.5 text-xs outline-none focus:border-teal" />
          <textarea value={form.systemPromptEn} onChange={(event) => setForm({ ...form, systemPromptEn: event.target.value })} placeholder="English system prompt" dir="ltr" rows={5} className="rounded-xl border border-ink/10 bg-white px-3 py-3 text-xs outline-none focus:border-teal lg:col-span-2" />
          <button type="button" onClick={() => createPrompt.mutate()} disabled={!form.nameAr || !form.key || form.systemPromptEn.length < 10 || createPrompt.isPending} className="rounded-xl bg-teal px-4 py-2.5 text-[10px] font-black text-white disabled:opacity-40 lg:col-span-2">
            إنشاء إصدار جديد
          </button>
        </div>
      )}

      <div className="space-y-3">
        {promptsQuery.data?.map((prompt) => (
          <article key={prompt.id} className="rounded-2xl border border-ink/10 bg-white p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 className="text-sm font-black">{prompt.nameAr}</h3>
                <p className="mt-1 text-[10px] font-semibold text-slate-500">{prompt.descriptionAr}</p>
              </div>
              <div className="flex gap-2">
                <span className="rounded-full bg-slate-50 px-2 py-1 text-[8px] font-black text-slate-500">
                  {promptLevelLabels[prompt.level]}
                </span>
                <span className="rounded-full bg-teal/10 px-2 py-1 text-[8px] font-black text-teal">
                  v{prompt.version}
                </span>
              </div>
            </div>
          </article>
        ))}
        {!promptsQuery.isLoading && !promptsQuery.data?.length && (
          <div className="rounded-2xl border border-dashed border-ink/15 py-14 text-center text-xs font-bold text-slate-500">
            لا توجد Prompts محفوظة بعد.
          </div>
        )}
      </div>
    </div>
  );
}

export function KnowledgePage() {
  const overviewQuery = useQuery({
    queryKey: ['knowledge-overview'],
    queryFn: () => api<Overview>('/api/knowledge/overview'),
  });

  return (
    <div className="min-h-screen p-4 sm:p-8">
      <div className="mx-auto max-w-7xl">
        <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-black text-teal">العقل المؤسسي المحلي</p>
            <h1 className="mt-2 text-2xl font-black">المعرفة والتوجيهات</h1>
            <p className="mt-2 max-w-2xl text-xs font-semibold leading-6 text-slate-500">
              مكتبة موحدة للأوامر والدستور والمستندات وإصدارات Prompts دون ربط دائم بمزود خارجي.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
            {[
              { label: 'مجموعات', value: overviewQuery.data?.collections || 0, Icon: Library },
              { label: 'مستندات', value: overviewQuery.data?.documents || 0, Icon: FileText },
              { label: 'أوامر', value: overviewQuery.data?.commands || 0, Icon: Sparkles },
              { label: 'Prompts', value: overviewQuery.data?.prompts || 0, Icon: BookOpen },
              { label: 'أوضاع', value: overviewQuery.data?.modes || 0, Icon: ChevronLeft },
            ].map(({ label, value, Icon }) => (
              <div key={label} className="rounded-xl border border-ink/10 bg-white px-3 py-2 text-center">
                <Icon className="mx-auto mb-1 h-3.5 w-3.5 text-teal" />
                <strong className="block text-lg text-ink">{Number(value)}</strong>
                <span className="text-[8px] font-bold text-slate-400">{label}</span>
              </div>
            ))}
          </div>
        </header>

        <Tabs.Root defaultValue="library">
          <Tabs.List className="mb-5 flex gap-1 overflow-x-auto rounded-xl border border-ink/10 bg-white p-1">
            {([
              ['library', 'المكتبة المعرفية', Library],
              ['commands', 'مكتبة الأوامر', Sparkles],
              ['prompts', 'Prompt Vault', BookOpen],
            ] as const).map(([value, label, Icon]) => (
              <Tabs.Trigger
                key={String(value)}
                value={String(value)}
                className="inline-flex min-w-fit items-center gap-2 rounded-lg px-4 py-2.5 text-[11px] font-black text-slate-500 outline-none data-[state=active]:bg-ink data-[state=active]:text-white"
              >
                <Icon className="h-3.5 w-3.5" />
                {String(label)}
              </Tabs.Trigger>
            ))}
          </Tabs.List>
          <Tabs.Content value="library" className="outline-none">
            <div className="overflow-hidden rounded-2xl border border-ink/10 bg-white/60">
              <KnowledgeLibrary />
            </div>
          </Tabs.Content>
          <Tabs.Content value="commands" className="outline-none"><CommandLibrary /></Tabs.Content>
          <Tabs.Content value="prompts" className="outline-none"><PromptVault /></Tabs.Content>
        </Tabs.Root>
      </div>
    </div>
  );
}
